import { useEffect, useState } from "react";
import { ArrowDown } from "react-feather";
import { message, notification } from "antd";

import styles from "./RulesTab.module.css";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useGetActiveRules } from "../../../../queries/withdrawal-rules-v2/useGetActiveRules";

import {
  TIER_1_RULE_IDS,
  TIER_2_RULE_IDS,
} from "../../../../queries/withdrawal-rules-v2/mappers";
import { Input, Select } from "../../../shared";
import RulePanel from "./RulePanel/RulePanel";
import DecisionGraph from "./DecisionGraph/DecisionGraph";
import VersionsDrawer from "./VersionsDrawer/VersionsDrawer";
import { useRulesEditorState } from "./useRulesEditorState";
import { useProductFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { TRIVELTA_PRODUCTS } from "../../../../utils/products";

const PERIOD_OPTIONS = [
  { value: "HOUR", label: AppStrings.PERIOD_HOUR },
  { value: "DAY", label: AppStrings.PERIOD_DAY },
  { value: "WEEK", label: AppStrings.PERIOD_WEEK },
  { value: "MONTH", label: AppStrings.PERIOD_MONTH },
  { value: "YEAR", label: AppStrings.PERIOD_YEAR },
];

const COLUMN_LABELS = {
  rule: AppStrings.TIER_1_COLUMN_RULE,
  configuration: AppStrings.TIER_1_COLUMN_CONFIGURATION,
  status: AppStrings.TIER_1_COLUMN_STATUS,
};

// ─── Tier 1 — Trigger Rules ────────────────────────────────────────────────

const TIER_1_RULES = [
  {
    id: TIER_1_RULE_IDS.MAX_AMOUNT,
    name: AppStrings.TIER_1_RULE_MAX_AMOUNT_NAME,
    description: AppStrings.TIER_1_RULE_MAX_AMOUNT_DESCRIPTION,
  },
  {
    id: TIER_1_RULE_IDS.FIRST_TIME_WITHDRAWAL,
    name: AppStrings.TIER_1_RULE_FIRST_TIME_WITHDRAWAL_NAME,
    description: AppStrings.TIER_1_RULE_FIRST_TIME_WITHDRAWAL_DESCRIPTION,
  },
  {
    id: TIER_1_RULE_IDS.WITHDRAWAL_PER_DAY,
    name: AppStrings.TIER_1_RULE_WITHDRAWAL_PER_DAY_NAME,
    description: AppStrings.TIER_1_RULE_WITHDRAWAL_PER_DAY_DESCRIPTION,
  },
  {
    id: TIER_1_RULE_IDS.WITHDRAWALS_SINCE_DEPOSIT,
    name: AppStrings.TIER_1_RULE_WITHDRAWALS_SINCE_DEPOSIT_NAME,
    description:
      AppStrings.TIER_1_RULE_WITHDRAWALS_SINCE_DEPOSIT_DESCRIPTION,
  },
  {
    id: TIER_1_RULE_IDS.X_TIMES_LAST_DEPOSIT,
    name: AppStrings.TIER_1_RULE_X_TIMES_LAST_DEPOSIT_NAME,
    description: AppStrings.TIER_1_RULE_X_TIMES_LAST_DEPOSIT_DESCRIPTION,
  },
];

// Tier 1 toggles map 1:1 to the BE feature vocabulary:
//   MAX_AMOUNT                → withdrawal_amount (single-value; period is
//                               deliberately absent — the spec's
//                               `withdrawal_amount` has no windowed variant)
//   FIRST_TIME_WITHDRAWAL     → is_first_time_withdrawal
//   WITHDRAWAL_PER_DAY        → withdrawals_per_day_<days>d compared with a
//                               threshold ("Flag when daily withdrawals
//                               exceed a threshold across a rolling day
//                               window.")
//   WITHDRAWALS_SINCE_DEPOSIT → withdrawals_since_last_deposit
//   X_TIMES_LAST_DEPOSIT      → current_withdrawal_to_last_deposit_ratio

// ─── Tier 2 — Auto Approve ────────────────────────────────────────────────

// Tier 2 toggles map 1:1 to the BE feature vocabulary (see PAM v2
// Withdrawal Rules spec, section "Feature names"):
//   REVIEWED_X_TIMES        → prior_manual_review_count_<within><period>
//   VIP_MATCHING            → is_vip AND same_instrument_success_count_<within><period>
//   SAME_INSTRUMENT_X_TIMES → same_instrument_success_count_<within><period>
//   MIN_ACCOUNT_AGE         → account_age_days
//   DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK
//     → sole_deposit_payment_instrument_<window> == "spei"
// The prior SAME_INSTRUMENT_WINDOW toggle was collapsed into
// SAME_INSTRUMENT_X_TIMES (it was the `uses = 1` special case).

const TIER_2_RULES = [
  {
    id: TIER_2_RULE_IDS.REVIEWED_X_TIMES,
    name: AppStrings.TIER_2_RULE_REVIEWED_X_TIMES_NAME,
    description: AppStrings.TIER_2_RULE_REVIEWED_X_TIMES_DESCRIPTION,
  },
  {
    id: TIER_2_RULE_IDS.VIP_MATCHING,
    name: AppStrings.TIER_2_RULE_VIP_MATCHING_NAME,
    description: AppStrings.TIER_2_RULE_VIP_MATCHING_DESCRIPTION,
  },
  {
    id: TIER_2_RULE_IDS.SAME_INSTRUMENT_X_TIMES,
    name: AppStrings.TIER_2_RULE_SAME_INSTRUMENT_X_TIMES_NAME,
    description: AppStrings.TIER_2_RULE_SAME_INSTRUMENT_X_TIMES_DESCRIPTION,
  },
  {
    id: TIER_2_RULE_IDS.MIN_ACCOUNT_AGE,
    name: AppStrings.TIER_2_RULE_MIN_ACCOUNT_AGE_NAME,
    description: AppStrings.TIER_2_RULE_MIN_ACCOUNT_AGE_DESCRIPTION,
  },
  {
    id: TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK,
    name: AppStrings.WITHDRAWAL_TIER_2_PAYMENT_INSTRUMENT_LOOKBACK_NAME,
    description:
      AppStrings.WITHDRAWAL_TIER_2_PAYMENT_INSTRUMENT_LOOKBACK_DESCRIPTION,
  },
];

// Loading / error / empty / success state machine for the debug
// panel body. Extracted so RulesTab.jsx doesn't grow a nested ternary
// tree inline — the four states each get one explicit branch and one
// stable `data-testid` for tests.
function DebugGraphBody() {
  const {
    data: graph,
    isPending,
    isError,
  } = useGetActiveRules();

  if (isPending) {
    return (
      <p
        className={styles.debugPanelGraphState}
        data-testid="rules-debug-graph-loading"
      >
        {AppStrings.RULES_DEBUG_GRAPH_LOADING}
      </p>
    );
  }

  if (isError) {
    return (
      <p
        className={styles.debugPanelGraphState}
        data-testid="rules-debug-graph-error"
        role="alert"
      >
        {AppStrings.RULES_DEBUG_GRAPH_ERROR}
      </p>
    );
  }

  // BE returns 404 when nothing has been published yet — the hook
  // translates that to `null`. Not an error, so no `role="alert"`.
  if (!graph) {
    return (
      <p
        className={styles.debugPanelGraphState}
        data-testid="rules-debug-graph-empty"
      >
        {AppStrings.RULES_DEBUG_GRAPH_EMPTY}
      </p>
    );
  }

  return (
    <DecisionGraph
      graph={graph}
      ariaLabel={AppStrings.RULES_DEBUG_GRAPH_PANEL_TITLE}
    />
  );
}

function buildEnabledMap(state) {
  return Object.fromEntries(
    Object.entries(state).map(([id, rule]) => [id, !!rule.enabled]),
  );
}

function buildInvalidMap(validation, tier) {
  if (!validation || validation.ok) return {};
  const invalid = {};
  for (const err of validation.errors) {
    if (err.tier === tier) invalid[err.ruleId] = true;
  }
  return invalid;
}

/**
 * Map an axios error's status code to the right toast copy. Kept as
 * a pure function so it is trivially testable and doesn't need to
 * live inside `RulesTab`.
 */
function pickPublishErrorToast(status) {
  if (status === 409) return AppStrings.RULES_EDITOR_PUBLISH_CONFLICT_TOAST;
  if (status === 422)
    return AppStrings.RULES_EDITOR_PUBLISH_VALIDATION_TOAST;
  return AppStrings.RULES_EDITOR_PUBLISH_ERROR_GENERIC_TOAST;
}

/** Human-readable summary for the action-bar status line. */
function pickStatusLine(editor) {
  if (!editor.validation || !editor.validation.ok) {
    const count = editor.validation ? editor.validation.errors.length : 0;
    if (count === 1) return AppStrings.RULES_EDITOR_STATUS_INVALID_ONE;
    return AppStrings.RULES_EDITOR_STATUS_INVALID_MANY.replace(
      "{count}",
      String(count),
    );
  }
  if (!editor.isDirty) return AppStrings.RULES_EDITOR_STATUS_CLEAN;
  return AppStrings.RULES_EDITOR_STATUS_DIRTY_VALID;
}

export default function RulesTab() {
  const { isEnabled: isSpeiWithdrawalReviewRuleEnabled } =
    useProductFeatureFlag(
      "SPEI_Withdrawal_Review_Rule",
      TRIVELTA_PRODUCTS.PAYMENTS,
    );
  const editor = useRulesEditorState({
    isDepositPaymentInstrumentRuleEnabled:
      isSpeiWithdrawalReviewRuleEnabled,
  });
  const tier2Rules = isSpeiWithdrawalReviewRuleEnabled
    ? TIER_2_RULES
    : TIER_2_RULES.filter(
        ({ id }) =>
          id !== TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK,
      );
  const paymentInstrumentLookbackOptions = [
    {
      value: "24h",
      label:
        AppStrings.WITHDRAWAL_TIER_2_PAYMENT_INSTRUMENT_LOOKBACK_24_HOURS,
    },
    {
      value: "7d",
      label: AppStrings.WITHDRAWAL_TIER_2_PAYMENT_INSTRUMENT_LOOKBACK_7_DAYS,
    },
    {
      value: "30d",
      label: AppStrings.WITHDRAWAL_TIER_2_PAYMENT_INSTRUMENT_LOOKBACK_30_DAYS,
    },
    {
      value: "90d",
      label: AppStrings.WITHDRAWAL_TIER_2_PAYMENT_INSTRUMENT_LOOKBACK_90_DAYS,
    },
  ];
  // Debug-only DecisionGraph viewer at the bottom of the tab. Lives
  // here (rather than as a separate tab) so engineers can compare the
  // toggle state above with the published graph in one screen. The
  // outer route is `developmentMode: true`, so non-engineers never see
  // this surface.
  const [isGraphPanelOpen, setIsGraphPanelOpen] = useState(false);
  // Versions drawer state — a peer of the graph panel toggle. The
  // drawer's own render is gated by `isOpen`; we keep the state
  // here (rather than inside the drawer) so the toolbar button can
  // reason about aria-expanded and the drawer can be commanded to
  // close programmatically after a successful activation.
  const [isVersionsDrawerOpen, setIsVersionsDrawerOpen] = useState(false);

  // Fire a toast whenever the publish mutation transitions to a
  // terminal state. Kept in an effect (not in the click handler) so
  // that success/error branches only run ONCE per mutation cycle —
  // React Query settles isSuccess/isError on the next microtask, and
  // reading them synchronously right after `.mutate()` would miss.
  useEffect(() => {
    if (editor.publishState === "success") {
      notification.info({
        key: "rules-editor-publish-success",
        className: styles.publishNotification,
        message: AppStrings.RULES_EDITOR_PUBLISH_SUCCESS_NOTIFICATION_TITLE,
        description:
          AppStrings.RULES_EDITOR_PUBLISH_SUCCESS_NOTIFICATION_DESCRIPTION,
      });
    } else if (editor.publishState === "error") {
      const status = editor.publishError?.response?.status;
      message.error(pickPublishErrorToast(status));
    }
  }, [editor.publishState, editor.publishError]);

  const disabledForRow = (ruleEnabled) => editor.readOnly || !ruleEnabled;

  const renderTier1Configuration = (ruleId) => {
    if (!editor.state) return null;
    const rule = editor.state.tier1[ruleId];
    const disabled = disabledForRow(rule.enabled);
    const updateTier1Rule = editor.updateTier1Rule;

    switch (ruleId) {
      case TIER_1_RULE_IDS.MAX_AMOUNT:
        return (
          <div className={styles.configField}>
            <span className={styles.configLabel}>
              {AppStrings.TIER_1_RULE_MAX_AMOUNT_LABEL}
            </span>
            <Input
              type="number"
              min={0}
              className={styles.numberInput}
              placeholder={
                AppStrings.TIER_1_RULE_MAX_AMOUNT_NUMBER_PLACEHOLDER
              }
              value={rule.amount}
              onChange={(e) =>
                updateTier1Rule(ruleId, { amount: e.target.value })
              }
              disabled={disabled}
              aria-label={AppStrings.TIER_1_RULE_MAX_AMOUNT_LABEL}
              data-testid={`tier1-rule-${ruleId}-amount-input`}
            />
          </div>
        );

      case TIER_1_RULE_IDS.FIRST_TIME_WITHDRAWAL:
        return null;

      case TIER_1_RULE_IDS.WITHDRAWAL_PER_DAY:
        return (
          <>
            <div className={styles.configField}>
              <span className={styles.configLabel}>
                {AppStrings.TIER_1_RULE_WITHDRAWAL_PER_DAY_THRESHOLD_LABEL}
              </span>
              <Input
                type="number"
                min={0}
                className={styles.numberInput}
                placeholder={
                  AppStrings.TIER_1_RULE_WITHDRAWAL_PER_DAY_THRESHOLD_PLACEHOLDER
                }
                value={rule.threshold}
                onChange={(e) =>
                  updateTier1Rule(ruleId, { threshold: e.target.value })
                }
                disabled={disabled}
                aria-label={
                  AppStrings.TIER_1_RULE_WITHDRAWAL_PER_DAY_THRESHOLD_LABEL
                }
                data-testid={`tier1-rule-${ruleId}-threshold-input`}
              />
            </div>
            <div className={styles.configField}>
              <span className={styles.configLabel}>
                {AppStrings.TIER_1_RULE_WITHDRAWAL_PER_DAY_LABEL}
              </span>
              <Input
                type="number"
                min={0}
                className={styles.numberInput}
                placeholder={
                  AppStrings.TIER_1_RULE_WITHDRAWAL_PER_DAY_PLACEHOLDER
                }
                value={rule.days}
                onChange={(e) =>
                  updateTier1Rule(ruleId, { days: e.target.value })
                }
                disabled={disabled}
                aria-label={AppStrings.TIER_1_RULE_WITHDRAWAL_PER_DAY_LABEL}
                data-testid={`tier1-rule-${ruleId}-days-input`}
              />
            </div>
          </>
        );

      case TIER_1_RULE_IDS.WITHDRAWALS_SINCE_DEPOSIT:
        return (
          <div className={styles.configField}>
            <span className={styles.configLabel}>
              {AppStrings.TIER_1_RULE_WITHDRAWALS_SINCE_DEPOSIT_LABEL}
            </span>
            <Input
              type="number"
              min={0}
              className={styles.numberInput}
              placeholder={
                AppStrings.TIER_1_RULE_WITHDRAWALS_SINCE_DEPOSIT_PLACEHOLDER
              }
              value={rule.withdrawals}
              onChange={(e) =>
                updateTier1Rule(ruleId, { withdrawals: e.target.value })
              }
              disabled={disabled}
              aria-label={
                AppStrings.TIER_1_RULE_WITHDRAWALS_SINCE_DEPOSIT_LABEL
              }
              data-testid={`tier1-rule-${ruleId}-withdrawals-input`}
            />
          </div>
        );

      case TIER_1_RULE_IDS.X_TIMES_LAST_DEPOSIT:
        return (
          <div className={styles.configField}>
            <span className={styles.configLabel}>
              {AppStrings.TIER_1_RULE_X_TIMES_LAST_DEPOSIT_LABEL}
            </span>
            <Input
              type="number"
              min={0}
              className={styles.numberInput}
              placeholder={
                AppStrings.TIER_1_RULE_X_TIMES_LAST_DEPOSIT_PLACEHOLDER
              }
              value={rule.multiplier}
              onChange={(e) =>
                updateTier1Rule(ruleId, { multiplier: e.target.value })
              }
              disabled={disabled}
              aria-label={AppStrings.TIER_1_RULE_X_TIMES_LAST_DEPOSIT_LABEL}
              data-testid={`tier1-rule-${ruleId}-multiplier-input`}
            />
          </div>
        );

      default:
        return null;
    }
  };

  const renderTier2Configuration = (ruleId) => {
    if (!editor.state) return null;
    const rule = editor.state.tier2[ruleId];
    const disabled = disabledForRow(rule.enabled);
    const updateTier2Rule = editor.updateTier2Rule;

    switch (ruleId) {
      case TIER_2_RULE_IDS.REVIEWED_X_TIMES:
        return (
          <>
            <div className={styles.configField}>
              <span className={styles.configLabel}>
                {AppStrings.TIER_2_RULE_REVIEWED_X_TIMES_REVIEWS_LABEL}
              </span>
              <Input
                type="number"
                min={0}
                className={styles.numberInput}
                placeholder={
                  AppStrings.TIER_2_RULE_REVIEWED_X_TIMES_REVIEWS_PLACEHOLDER
                }
                value={rule.reviews}
                onChange={(e) =>
                  updateTier2Rule(ruleId, { reviews: e.target.value })
                }
                disabled={disabled}
                aria-label={
                  AppStrings.TIER_2_RULE_REVIEWED_X_TIMES_REVIEWS_LABEL
                }
                data-testid={`tier2-rule-${ruleId}-reviews-input`}
              />
            </div>
            <div className={styles.configField}>
              <span className={styles.configLabel}>
                {AppStrings.TIER_2_RULE_WITHIN_LABEL}
              </span>
              <div className={styles.configInputs}>
                <Input
                  type="number"
                  min={0}
                  className={styles.numberInput}
                  placeholder={AppStrings.TIER_2_RULE_WITHIN_PLACEHOLDER}
                  value={rule.within}
                  onChange={(e) =>
                    updateTier2Rule(ruleId, { within: e.target.value })
                  }
                  disabled={disabled}
                  aria-label={AppStrings.TIER_2_RULE_WITHIN_LABEL}
                  data-testid={`tier2-rule-${ruleId}-within-input`}
                />
                <Select
                  className={styles.periodSelect}
                  options={PERIOD_OPTIONS}
                  value={rule.period}
                  onChange={(value) =>
                    updateTier2Rule(ruleId, { period: value })
                  }
                  disabled={disabled}
                  data-testid={`tier2-rule-${ruleId}-period-select`}
                />
              </div>
            </div>
          </>
        );

      case TIER_2_RULE_IDS.VIP_MATCHING:
        // Renders only the `within` + `period` window inputs. The
        // `is_vip == true` half of the condition and the `>= 1`
        // count threshold on `same_instrument_success_count` are
        // implicit — the toggle's identity IS "VIP + at least one
        // prior successful redemption in the window".
        return (
          <div className={styles.configField}>
            <span className={styles.configLabel}>
              {AppStrings.TIER_2_RULE_WITHIN_LABEL}
            </span>
            <div className={styles.configInputs}>
              <Input
                type="number"
                min={0}
                className={styles.numberInput}
                placeholder={AppStrings.TIER_2_RULE_WITHIN_PLACEHOLDER}
                value={rule.within}
                onChange={(e) =>
                  updateTier2Rule(ruleId, { within: e.target.value })
                }
                disabled={disabled}
                aria-label={AppStrings.TIER_2_RULE_WITHIN_LABEL}
                data-testid={`tier2-rule-${ruleId}-within-input`}
              />
              <Select
                className={styles.periodSelect}
                options={PERIOD_OPTIONS}
                value={rule.period}
                onChange={(value) =>
                  updateTier2Rule(ruleId, { period: value })
                }
                disabled={disabled}
                data-testid={`tier2-rule-${ruleId}-period-select`}
              />
            </div>
          </div>
        );

      case TIER_2_RULE_IDS.SAME_INSTRUMENT_X_TIMES:
        return (
          <>
            <div className={styles.configField}>
              <span className={styles.configLabel}>
                {AppStrings.TIER_2_RULE_SAME_INSTRUMENT_X_TIMES_USES_LABEL}
              </span>
              <Input
                type="number"
                min={0}
                className={styles.numberInput}
                placeholder={
                  AppStrings.TIER_2_RULE_SAME_INSTRUMENT_X_TIMES_USES_PLACEHOLDER
                }
                value={rule.uses}
                onChange={(e) =>
                  updateTier2Rule(ruleId, { uses: e.target.value })
                }
                disabled={disabled}
                aria-label={
                  AppStrings.TIER_2_RULE_SAME_INSTRUMENT_X_TIMES_USES_LABEL
                }
                data-testid={`tier2-rule-${ruleId}-uses-input`}
              />
            </div>
            <div className={styles.configField}>
              <span className={styles.configLabel}>
                {AppStrings.TIER_2_RULE_WITHIN_LABEL}
              </span>
              <div className={styles.configInputs}>
                <Input
                  type="number"
                  min={0}
                  className={styles.numberInput}
                  placeholder={AppStrings.TIER_2_RULE_WITHIN_PLACEHOLDER}
                  value={rule.within}
                  onChange={(e) =>
                    updateTier2Rule(ruleId, { within: e.target.value })
                  }
                  disabled={disabled}
                  aria-label={AppStrings.TIER_2_RULE_WITHIN_LABEL}
                  data-testid={`tier2-rule-${ruleId}-within-input`}
                />
                <Select
                  className={styles.periodSelect}
                  options={PERIOD_OPTIONS}
                  value={rule.period}
                  onChange={(value) =>
                    updateTier2Rule(ruleId, { period: value })
                  }
                  disabled={disabled}
                  data-testid={`tier2-rule-${ruleId}-period-select`}
                />
              </div>
            </div>
          </>
        );

      case TIER_2_RULE_IDS.MIN_ACCOUNT_AGE:
        // The unit is hardcoded to days because the underlying BE
        // feature (`account_age_days`) is not windowed — its name
        // encodes the unit. Exposing a period selector here would
        // silently coerce to days at publish time and confuse admins.
        return (
          <div className={styles.configField}>
            <span className={styles.configLabel}>
              {AppStrings.TIER_2_RULE_MIN_ACCOUNT_AGE_LABEL}
            </span>
            <Input
              type="number"
              min={0}
              className={styles.numberInput}
              placeholder={
                AppStrings.TIER_2_RULE_MIN_ACCOUNT_AGE_PLACEHOLDER
              }
              value={rule.days}
              onChange={(e) =>
                updateTier2Rule(ruleId, { days: e.target.value })
              }
              disabled={disabled}
              aria-label={AppStrings.TIER_2_RULE_MIN_ACCOUNT_AGE_LABEL}
              data-testid={`tier2-rule-${ruleId}-days-input`}
            />
          </div>
        );

      case TIER_2_RULE_IDS.DEPOSIT_PAYMENT_INSTRUMENT_LOOKBACK:
        return (
          <>
            <div className={styles.configField}>
              <span className={styles.configLabel}>
                {
                  AppStrings.WITHDRAWAL_TIER_2_PAYMENT_INSTRUMENT_LOOKBACK_INSTRUMENT_LABEL
                }
              </span>
              <span
                className={styles.fixedValue}
                data-testid={`tier2-rule-${ruleId}-instrument-value`}
              >
                {
                  AppStrings.WITHDRAWAL_TIER_2_PAYMENT_INSTRUMENT_LOOKBACK_SPEI_VALUE
                }
              </span>
            </div>
            <div className={styles.configField}>
              <span className={styles.configLabel}>
                {
                  AppStrings.WITHDRAWAL_TIER_2_PAYMENT_INSTRUMENT_LOOKBACK_PERIOD_LABEL
                }
              </span>
              <Select
                className={styles.lookbackSelect}
                options={paymentInstrumentLookbackOptions}
                value={rule.lookback || undefined}
                placeholder={
                  AppStrings.WITHDRAWAL_TIER_2_PAYMENT_INSTRUMENT_LOOKBACK_PERIOD_PLACEHOLDER
                }
                onChange={(value) =>
                  updateTier2Rule(ruleId, { lookback: value })
                }
                disabled={disabled}
                aria-label={
                  AppStrings.WITHDRAWAL_TIER_2_PAYMENT_INSTRUMENT_LOOKBACK_PERIOD_LABEL
                }
                virtual={false}
                data-testid={`tier2-rule-${ruleId}-period-select`}
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={styles.container}
      data-testid="withdrawal-review-rules-tab-content"
    >
      <section
        className={styles.debugPanel}
        aria-label={AppStrings.RULES_DEBUG_GRAPH_SECTION_ARIA_LABEL}
        data-testid="rules-debug-graph-panel"
      >
        <header className={styles.debugPanelHeader}>
          <div className={styles.debugPanelTitleGroup}>
            <h3
              className={styles.debugPanelTitle}
              id="rules-debug-graph-title"
            >
              {AppStrings.RULES_DEBUG_GRAPH_PANEL_TITLE}
            </h3>
            <p className={styles.debugPanelSubtitle}>
              {AppStrings.RULES_DEBUG_GRAPH_PANEL_SUBTITLE}
            </p>
          </div>
          <button
            type="button"
            className={styles.debugPanelToggle}
            onClick={() => setIsGraphPanelOpen((prev) => !prev)}
            aria-expanded={isGraphPanelOpen}
            aria-controls="rules-debug-graph-body"
            data-testid="rules-debug-graph-toggle"
          >
            {isGraphPanelOpen
              ? AppStrings.RULES_DEBUG_GRAPH_HIDE
              : AppStrings.RULES_DEBUG_GRAPH_SHOW}
          </button>
        </header>

        {isGraphPanelOpen && (
          <div
            id="rules-debug-graph-body"
            className={styles.debugPanelGraph}
            data-testid="rules-debug-graph-body"
          >
            <DebugGraphBody />
          </div>
        )}
      </section>

      {renderEditorBody(editor, {
        tier2Rules,
        renderTier1Configuration,
        renderTier2Configuration,
        onOpenVersions: () => setIsVersionsDrawerOpen(true),
      })}

      {/*
        Only mount the drawer while open, otherwise its data hooks
        (`useListRuleVersions`, plus the per-version graph fetches
        that back each row's dropdown) would run on every RulesTab
        render and pay a network round-trip most admins never care
        about. antd's Drawer without a wrapping conditional would
        keep mounted for animation purposes; the trade-off here is
        losing the close-animation for a lighter default render.
      */}
      {isVersionsDrawerOpen && (
        <VersionsDrawer
          isOpen={isVersionsDrawerOpen}
          onClose={() => setIsVersionsDrawerOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Renders everything BELOW the debug panel: the read-only banner
 * (or nothing), the loading placeholder (or the toggle panels), and
 * the action bar with the Publish button.
 *
 * Kept as a free function so `RulesTab`'s render body stays flat and
 * so the three states (loading, read-only, normal) each get one
 * top-level branch that reads cleanly.
 */
function renderEditorBody(
  editor,
  {
    tier2Rules,
    renderTier1Configuration,
    renderTier2Configuration,
    onOpenVersions,
  },
) {
  if (editor.readOnly) {
    return (
      <div
        className={styles.readOnlyBanner}
        role="alert"
        data-testid="rules-editor-readonly-banner"
      >
        <h4 className={styles.readOnlyBannerTitle}>
          {AppStrings.RULES_EDITOR_READONLY_BANNER_TITLE}
        </h4>
        <p className={styles.readOnlyBannerMessage}>
          {AppStrings.RULES_EDITOR_READONLY_BANNER_MESSAGE}
        </p>
        <div className={styles.readOnlyBannerActions}>
          <button
            type="button"
            className={styles.readOnlyBannerButton}
            onClick={editor.dismissReadOnly}
            data-testid="rules-editor-readonly-start-fresh-button"
          >
            {AppStrings.RULES_EDITOR_READONLY_START_FRESH_BUTTON}
          </button>
          <span className={styles.readOnlyBannerHelper}>
            {AppStrings.RULES_EDITOR_READONLY_START_FRESH_HELPER}
          </span>
        </div>
      </div>
    );
  }

  if (editor.isLoading || !editor.state) {
    // `!editor.state` covers the "GET failed with 5xx" case as well:
    // the hook does not hydrate on error, so state stays undefined
    // and we render the same skeleton copy. A dedicated error banner
    // could be added if error-vs-loading becomes an important
    // distinction, but for a first cut the loading text is enough.
    return (
      <p
        className={styles.editorLoading}
        data-testid="rules-editor-loading"
      >
        {editor.loadError
          ? AppStrings.RULES_EDITOR_LOAD_ERROR
          : AppStrings.RULES_EDITOR_LOADING}
      </p>
    );
  }

  const canPublish =
    editor.isDirty &&
    editor.validation?.ok === true &&
    editor.publishState !== "pending";

  const canCancel = editor.isDirty && editor.publishState !== "pending";

  return (
    <>
      <RulePanel
        title={AppStrings.TIER_1_TRIGGER_RULES_TITLE}
        titleId="tier1-trigger-rules-title"
        tooltip={AppStrings.TIER_1_TRIGGER_RULES_TOOLTIP}
        tooltipLabel={AppStrings.TIER_1_TRIGGER_RULES_TOOLTIP_LABEL}
        subtitle={AppStrings.TIER_1_TRIGGER_RULES_SUBTITLE}
        subtitleTone="warning"
        allOnLabel={AppStrings.TIER_1_ALL_ON}
        toggleAriaPrefix={AppStrings.TIER_1_RULE_TOGGLE_ARIA}
        rules={TIER_1_RULES}
        enabledMap={buildEnabledMap(editor.state.tier1)}
        invalidMap={buildInvalidMap(editor.validation, "tier_1")}
        onToggleRule={(id, enabled) =>
          editor.updateTier1Rule(id, { enabled })
        }
        onToggleAll={editor.toggleTier1All}
        renderConfiguration={renderTier1Configuration}
        testIdPrefix="tier1"
        columnLabels={COLUMN_LABELS}
      />

      <div
        className={styles.tierConnector}
        data-testid="tier-1-to-2-connector"
      >
        <p className={styles.tierConnectorText}>
          {AppStrings.TIER_1_TO_TIER_2_CONNECTOR}
        </p>
        <ArrowDown
          size={20}
          className={styles.tierConnectorArrow}
          aria-label={AppStrings.TIER_1_TO_TIER_2_CONNECTOR_ARROW_LABEL}
        />
      </div>

      <RulePanel
        title={AppStrings.TIER_2_AUTO_APPROVE_TITLE}
        titleId="tier2-auto-approve-title"
        tooltip={AppStrings.TIER_2_AUTO_APPROVE_TOOLTIP}
        tooltipLabel={AppStrings.TIER_2_AUTO_APPROVE_TOOLTIP_LABEL}
        subtitle={AppStrings.TIER_2_AUTO_APPROVE_SUBTITLE}
        subtitleTone="muted"
        allOnLabel={AppStrings.TIER_2_ALL_ON}
        toggleAriaPrefix={AppStrings.TIER_2_RULE_TOGGLE_ARIA}
        rules={tier2Rules}
        enabledMap={buildEnabledMap(editor.state.tier2)}
        invalidMap={buildInvalidMap(editor.validation, "tier_2")}
        onToggleRule={(id, enabled) =>
          editor.updateTier2Rule(id, { enabled })
        }
        onToggleAll={editor.toggleTier2All}
        renderConfiguration={renderTier2Configuration}
        testIdPrefix="tier2"
        columnLabels={COLUMN_LABELS}
        // Cross-tier gate: Tier 2 only ever reviews withdrawals a
        // Tier-1 trigger has flagged, so with zero enabled T1 rules
        // there is nothing for T2 to act on. The hook cascades T2
        // rules to `enabled: false` alongside setting this flag, so
        // by the time the panel renders locked its toggles are
        // already off — the disable + copy just tell the admin why.
        lockedMessage={
          editor.isTier2Locked
            ? AppStrings.TIER_2_LOCKED_BY_TIER_1_MESSAGE
            : null
        }
      />

      <section
        className={styles.actionBar}
        data-testid="rules-editor-action-bar"
      >
        <p
          className={styles.actionBarStatus}
          data-testid="rules-editor-status-line"
        >
          {pickStatusLine(editor)}
        </p>
        <div className={styles.actionBarButtons}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={onOpenVersions}
            data-testid="rules-editor-versions-button"
          >
            {AppStrings.RULES_VERSIONS_OPEN_BUTTON}
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={editor.cancel}
            disabled={!canCancel}
            data-testid="rules-editor-cancel-button"
          >
            {AppStrings.RULES_EDITOR_CANCEL_LABEL}
          </button>
          <button
            type="button"
            className={styles.publishButton}
            onClick={editor.publish}
            disabled={!canPublish}
            data-testid="rules-editor-publish-button"
          >
            {editor.publishState === "pending"
              ? AppStrings.RULES_EDITOR_PUBLISHING_LABEL
              : AppStrings.RULES_EDITOR_PUBLISH_LABEL}
          </button>
        </div>
      </section>
    </>
  );
}
